import { fetchCurrentUser } from '../../ducks/user.duck';
import { denormalisedEntities, updatedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { entityRefs, getImageVariants, isArrayLength } from '../../util/genericHelpers';
import { parse } from '../../util/urlHelpers';

import { types as sdkTypes } from '../../util/sdkLoader';
import { fetchUserShippingAddress, sortedTransactions } from '../../util/helperFunctions';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { extractImageUrl, listingRelationship } from '../../util/dataExtractor';
import {
  fetchMessages,
  fetchNextTransitions,
  setInitialValues,
} from '../TransactionPage/TransactionPage.duck';
const { Money, UUID } = sdkTypes;
const INBOX_PAGE_SIZE = 100;

// ================ Action types ================ //

const ActionTypes = {
  SET_INITIAL_STATE: 'app/DetailedInboxPage/SET_INITIAL_STATE',

  FETCH_TRANSACTIONS_REQUEST: 'app/DetailedInboxPage/FETCH_TRANSACTIONS_REQUEST',
  FETCH_TRANSACTIONS_SUCCESS: 'app/DetailedInboxPage/FETCH_TRANSACTIONS_SUCCESS',
  FETCH_TRANSACTIONS_ERROR: 'app/DetailedInboxPage/FETCH_TRANSACTIONS_ERROR',

  FETCH_CURRENT_TRANSACTION_REQUEST: 'app/DetailedInboxPage/ FETCH_CURRENT_TRANSACTION_REQUEST',
  FETCH_CURRENT_TRANSACTION_SUCCESS: 'app/DetailedInboxPage/FETCH_CURRENT_TRANSACTION_SUCCESS',
  FETCH_CURRENT_TRANSACTION_ERROR: 'app/DetailedInboxPage/FETCH_CURRENT_TRANSACTION_ERROR',

  PROVIDER_SHIPPING_ADDRESS: 'app/DetailedInboxPage/PROVIDER_SHIPPING_ADDRESS',

  UPLOAD_IMAGE_REQUEST: 'app/DetailedInboxPage/UPLOAD_IMAGE_REQUEST',
  UPLOAD_IMAGE_SUCCESS: 'app/DetailedInboxPage/UPLOAD_IMAGE_SUCCESS',
  UPLOAD_IMAGE_ERROR: 'app/DetailedInboxPage/UPLOAD_IMAGE_ERROR',

  FETCH_MORE_TRANSACTIONS_REQUEST: 'app/DetailedInboxPage/FETCH_MORE_TRANSACTIONS_REQUEST',
  FETCH_MORE_TRANSACTIONS_SUCCESS: 'app/DetailedInboxPage/FETCH_MORE_TRANSACTIONS_SUCCESS',
  FETCH_MORE_TRANSACTIONS_ERROR: 'app/DetailedInboxPage/FETCH_MORE_TRANSACTIONS_ERROR',
};

// ================ Initial State ================ //

const initialState = {
  uploadImageError: null,
  uploadInProgress: false,
  fetchtTransactionsInProgress: false,
  fetchTransactionsError: null,
  fetchCurrentTxnInProgress: false,
  fetchCurrentTxnError: null,
  pagination: null,
  transactionRefs: [],
  currentTransaction: null,
  providerShippingAddress: null,
};

// ================ Reducers ================ //

const DetailedInboxPageReducer = (state = initialState, { type, payload } = {}) => {
  switch (type) {
    case ActionTypes.SET_INITIAL_STATE:
      return { ...initialState };
    case ActionTypes.UPLOAD_IMAGE_REQUEST:
      return { ...state, uploadInProgress: true, uploadImageError: null };
    case ActionTypes.UPLOAD_IMAGE_SUCCESS:
      return { ...state, uploadInProgress: false };
    case ActionTypes.UPLOAD_IMAGE_ERROR:
      return { ...state, uploadInProgress: false, uploadImageError: payload };

    case ActionTypes.FETCH_TRANSACTIONS_REQUEST:
      return { ...state, fetchtTransactionsInProgress: true, fetchTransactionsError: null };
    case ActionTypes.FETCH_TRANSACTIONS_SUCCESS:
      const transactions = sortedTransactions(payload.data.data);

      return {
        ...state,
        fetchtTransactionsInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    case ActionTypes.FETCH_TRANSACTIONS_ERROR:
      return { ...state, fetchtTransactionsInProgress: false, fetchTransactionsError: payload };

    case ActionTypes.FETCH_CURRENT_TRANSACTION_REQUEST:
      return { ...state, fetchCurrentTxnInProgress: true, fetchCurrentTxnError: null };
    case ActionTypes.FETCH_CURRENT_TRANSACTION_SUCCESS:
      return {
        ...state,
        fetchCurrentTxnInProgress: false,
        currentTransaction: payload,
      };
    case ActionTypes.FETCH_CURRENT_TRANSACTION_ERROR:
      return { ...state, fetchCurrentTxnInProgress: false, fetchCurrentTxnError: payload };

    case ActionTypes.PROVIDER_SHIPPING_ADDRESS:
      return { ...state, providerShippingAddress: payload };
    case ActionTypes.FETCH_MORE_TRANSACTIONS_REQUEST:
      return { ...state, fetchtTransactionsInProgress: true, fetchTransactionsError: null };

    case ActionTypes.FETCH_MORE_TRANSACTIONS_SUCCESS: {
      const moreTransactions = sortedTransactions(payload.data.data);

      return {
        ...state,
        fetchtTransactionsInProgress: false,
        // append new refs after existing ones
        transactionRefs: [...state.transactionRefs, ...entityRefs(moreTransactions)],
        pagination: payload.data.meta,
      };
    }

    case ActionTypes.FETCH_MORE_TRANSACTIONS_ERROR:
      return { ...state, fetchtTransactionsInProgress: false, fetchTransactionsError: payload };
    default:
      return state;
  }
};

// ================ Selectors ================ //

export const uploadImageInProgressSelector = state => state.DetailedInboxPage.uploadInProgress;
export const uploadImageErrorSelector = state => state.DetailedInboxPage.uploadImageError;
export const fetchTransactionsInProgressSelector = state =>
  state.DetailedInboxPage.fetchtTransactionsInProgress;
export const fetchTransactionsErrorSelector = state =>
  state.DetailedInboxPage.fetchTransactionsError;
export const transactionRefsSelector = state => state.DetailedInboxPage.transactionRefs;
export const paginationSelector = state => state.DetailedInboxPage.pagination;

export const currentTransactionSelector = state => state.DetailedInboxPage.currentTransaction;
export const fetchCurrentTxnInProgressSelector = state =>
  state.DetailedInboxPage.fetchCurrentTxnInProgress;
export const fetchCurrentTxnErrorSelector = state => state.DetailedInboxPage.fetchCurrentTxnError;
export const providerShippingAddressSelector = state =>
  state.DetailedInboxPage.providerShippingAddress;
// ================ Actions Creators ================ //

const uploadImageRequest = () => ({ type: ActionTypes.UPLOAD_IMAGE_REQUEST });
const uploadImageSuccess = () => ({ type: ActionTypes.UPLOAD_IMAGE_SUCCESS });
const uploadImageError = error => ({
  type: ActionTypes.UPLOAD_IMAGE_ERROR,
  error: true,
  payload: error,
});

const fetchTransactionsRequest = () => ({ type: ActionTypes.FETCH_TRANSACTIONS_REQUEST });
const fetchTransactionsSuccess = response => ({
  type: ActionTypes.FETCH_TRANSACTIONS_SUCCESS,
  payload: response,
});
const fetchTransactionsError = error => ({
  type: ActionTypes.FETCH_TRANSACTIONS_ERROR,
  error: true,
  payload: error,
});

const fetchCurrentTransactionRequest = () => ({
  type: ActionTypes.FETCH_CURRENT_TRANSACTION_REQUEST,
});
const fetchCurrentTransactionSuccess = response => ({
  type: ActionTypes.FETCH_CURRENT_TRANSACTION_SUCCESS,
  payload: response,
});
const fetchCurrentTransactionError = error => ({
  type: ActionTypes.FETCH_CURRENT_TRANSACTION_ERROR,
  error: true,
  payload: error,
});
export const setInitialState = () => ({
  type: ActionTypes.SET_INITIAL_STATE,
});

const setProviderShippingAddress = address => ({
  type: ActionTypes.PROVIDER_SHIPPING_ADDRESS,
  payload: address,
});

const fetchMoreTransactionsRequest = () => ({
  type: ActionTypes.FETCH_MORE_TRANSACTIONS_REQUEST,
});

const fetchMoreTransactionsSuccess = response => ({
  type: ActionTypes.FETCH_MORE_TRANSACTIONS_SUCCESS,
  payload: response,
});

const fetchMoreTransactionsError = error => ({
  type: ActionTypes.FETCH_MORE_TRANSACTIONS_ERROR,
  error: true,
  payload: error,
});

// ================ Thunks ================ //

export const uploadImageUsingSdk = file => async (dispatch, getState, sdk) => {
  dispatch(uploadImageRequest());
  try {
    const response = await sdk.images.upload(
      {
        image: file,
      },
      {
        expand: true,
      }
    );
    dispatch(uploadImageSuccess());
    const imageUrl = extractImageUrl(response);
    return imageUrl;
  } catch (error) {
    dispatch(uploadImageError(storableError(error)));
  }
};

export const fetchTransactions = params => {
  return async (dispatch, getState, sdk) => {
    dispatch(fetchTransactionsRequest());

    try {
      const response = await sdk.transactions.query(params);
      dispatch(fetchTransactionsSuccess(response));
      dispatch(addMarketplaceEntities(response));

      return response;
    } catch (error) {
      dispatch(fetchTransactionsError(storableError(error)));
    }
  };
};

export const fetchMoreTransactions = () => {
  return async (dispatch, getState, sdk) => {
    const state = getState();
    const pagination = state.DetailedInboxPage.pagination;

    // No pagination yet → nothing to do
    if (!pagination) return;

    const currentPage = pagination.page || 1;
    const totalPages = pagination.totalPages || 1;

    // Already at last page
    if (currentPage >= totalPages) return;

    const nextPage = currentPage + 1;

    dispatch(fetchMoreTransactionsRequest());

    const apiQueryParams = {
      include: [
        'listing',
        'listing.images',
        'provider',
        'provider.profileImage',
        'customer',
        'customer.profileImage',
        'booking',
      ],
      'fields.transaction': [
        'createdAt',
        'processName',
        'lastTransition',
        'lastTransitionedAt',
        'transitions',
        'payinTotal',
        'payoutTotal',
        'lineItems',
      ],
      'fields.listing': ['title', 'availabilityPlan', 'publicData.listingType'],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'deleted', 'banned'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
      page: nextPage,
      perPage: INBOX_PAGE_SIZE,
    };

    try {
      const response = await sdk.transactions.query(apiQueryParams);
      dispatch(fetchMoreTransactionsSuccess(response));
      dispatch(addMarketplaceEntities(response));
      return response;
    } catch (error) {
      dispatch(fetchMoreTransactionsError(storableError(error)));
    }
  };
};

export const fetchCurrentTransaction = (txId, config) => {
  return async (dispatch, getState, sdk) => {
    dispatch(fetchCurrentTransactionRequest());

    try {
      const queryParams = {
        id: txId,
        include: [
          'customer',
          'customer.profileImage',
          'provider',
          'provider.profileImage',
          'listing',
          'listing.author',
          'listing.currentStock',
          'listing.images',
          'booking',
          'reviews',
          'reviews.author',
          'reviews.subject',
        ],
        ...getImageVariants(config.layout.listingImage),
      };
      const response = await sdk.transactions.show(queryParams, { expand: true });
      const listingId = listingRelationship(response).id;
      const entities = updatedEntities({}, response.data);
      const listingRef = { id: listingId, type: 'listing' };
      const transactionRef = { id: txId, type: 'transaction' };
      const denormalised = denormalisedEntities(entities, [listingRef, transactionRef]);
      const transaction = denormalised[1];
      const providerId = transaction?.provider?.id?.uuid;
      if (providerId) {
        const response = await fetchUserShippingAddress(providerId);
        dispatch(setProviderShippingAddress(response.shippingAddress));
      }
      dispatch(fetchCurrentTransactionSuccess(transaction));

      return response;
    } catch (error) {
      dispatch(fetchCurrentTransactionError(storableError(error)));
    }
  };
};

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  dispatch(setInitialState());
  dispatch(setInitialValues({}));

  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const { listingState, page = 1, ...rest } = queryParams || {};
  const transactionId = new UUID(params.txId);

  const apiQueryParams = {
    include: [
      'listing',
      'listing.images',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
    ],
    'fields.transaction': [
      'createdAt',
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
    ],
    'fields.listing': ['title', 'availabilityPlan', 'publicData.listingType'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'deleted', 'banned'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    page,
    perPage: INBOX_PAGE_SIZE,
  };
  return Promise.all([
    dispatch(fetchTransactions(apiQueryParams)),
    dispatch(fetchCurrentTransaction(transactionId, config)),
    dispatch(fetchMessages(transactionId, 1, config)),
    dispatch(fetchNextTransitions(transactionId)),
    dispatch(fetchCurrentUser({ enforce: true })),
  ]);
};

export default DetailedInboxPageReducer;
